import { useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import CruiseCabinTypeInfoModal from './Modals/CruiseCabinTypeInfoModal'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { CABIN_CATEGORIES_SORT_ORDER, CruiseType } from 'constants/cruise'
import { setSearchParamValue, deleteSearchParams } from 'lib/url/searchUrlUtils'
import { replace } from 'connected-react-router'
import { useLocation } from 'react-router-dom'
import { useAppDispatch } from 'hooks/reduxHooks'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'
import ToggleOptionsGroup from 'LuxKit/ToggleOptionsGroup'
import ToggleOption from 'LuxKit/ToggleOption'
import useCruiseSelectedCabinType from 'hooks/useCruiseSelectedCabinType'
import useCruiseFacetFiltersParams from 'hooks/useCruiseFacetFiltersParams'
import styled from 'styled-components'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'

const ToggleOptionContainer = styled.div`
  overflow-x: scroll;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

export type CruiseTypeWithBoth = App.CruiseType | 'BOTH'

/**
 * The cabin types we expect to receive from svc-search.
 */
type CABIN_CATEGORY_TYPE = 'Inside' | 'Oceanview' | 'Window' | 'Oceanview / Window' | 'Balcony' | 'Suite'
const CABIN_CATEGORIES: Array<CABIN_CATEGORY_TYPE> = ['Inside', 'Oceanview', 'Window', 'Oceanview / Window', 'Balcony', 'Suite']

/**
 * The cabin types we expect to display in the UI depending on the filtered in cruise type.
 */
export type CABIN_CATEGORY_LABEL_TYPE_RIVER = 'Window' | 'Balcony' | 'Suite'
export type CABIN_CATEGORY_LABEL_TYPE_BOTH = 'Inside' | 'Oceanview / Window' | 'Balcony' | 'Suite'
const CABIN_CATEGORIES_LABELS_RIVER: Array<CABIN_CATEGORY_LABEL_TYPE_RIVER> = ['Window', 'Balcony', 'Suite']
const CABIN_CATEGORIES_LABELS_BOTH: Array<CABIN_CATEGORY_LABEL_TYPE_BOTH> = ['Inside', 'Oceanview / Window', 'Balcony', 'Suite']

export type CabinTypeMap = {
  [key in CABIN_CATEGORY_TYPE]: {
    offerId: string
    minimumPrice: number
    minimumPricePerNight: number
  }
}

interface CruiseSelectCabinTypeProps {
  filters: App.OfferListFilters
  isMobile?: boolean
}

function CruiseSelectCabinType({ filters, isMobile }: CruiseSelectCabinTypeProps) {
  const intl = useIntl()
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const dispatch = useAppDispatch()
  const isPricePerNight = useCruisePriceByNight()
  const showModal = useContext(ModalContext)
  const { search } = useLocation()
  const { facetParams } = useCruiseFacetFiltersParams({ filters })
  const cabinType = useCruiseSelectedCabinType()
  const [selectedCabinType, setSelectedCabinType] = useState<string | undefined>(cabinType)
  const priceProperties = isPricePerNight ? 'minimumPricePerNight' : 'minimumPrice'
  const [facets] = useCruiseSearchFacets(facetParams)

  const cruiseType = useMemo(() => {
    const cruiseTypeFacets = facets.filter(facet => facet.category === 'cruise_types' && facet.count)

    if (cruiseTypeFacets.length === 1) {
      return cruiseTypeFacets[0].name as CruiseTypeWithBoth
    }

    return 'BOTH'
  }, [facets])

  const cabinCategories = useMemo(() => {
    if (cruiseType === CruiseType.RIVER) {
      return CABIN_CATEGORIES_LABELS_RIVER
    } else if (cruiseType === 'BOTH') {
      return CABIN_CATEGORIES_LABELS_BOTH
    }

    return CABIN_CATEGORIES_SORT_ORDER
  }, [cruiseType])

  const mappedCabinTypes = useMemo(() => {
    let minPriceOfOceanviewAndWindow: number | undefined = undefined
    let minPricePerNightOfOceanviewAndWindow: number | undefined = undefined

    return facets.reduce((cabinTypes, facet) => {
      if (CABIN_CATEGORIES.includes(facet.name as CABIN_CATEGORY_TYPE)) {
        // This is to cater for the case where Oceanview and Window are combined into Oceanview / Window when there are both ocean and river cruises in search.
        if (cruiseType === 'BOTH' && (facet.name === 'Oceanview' || facet.name === 'Window')) {
          if (!minPriceOfOceanviewAndWindow || (facet.min && minPriceOfOceanviewAndWindow > facet.min)) {
            minPriceOfOceanviewAndWindow = facet.min
            minPricePerNightOfOceanviewAndWindow = facet.minPerNight

            cabinTypes['Oceanview / Window' as CABIN_CATEGORY_TYPE] = {
              offerId: facet.value ?? '',
              minimumPrice: minPriceOfOceanviewAndWindow ?? 0,
              minimumPricePerNight: minPricePerNightOfOceanviewAndWindow ?? 0,
            }
          }
        } else {
          cabinTypes[facet.name as CABIN_CATEGORY_TYPE] = {
            offerId: facet.value ?? '',
            minimumPrice: facet.min ?? 0,
            minimumPricePerNight: facet.minPerNight ?? 0,
          }
        }
      }

      return cabinTypes
    }, {} as CabinTypeMap)
  }, [facets, cruiseType])

  const handleSelectedCabinType = useCallback((nextCabinType: string) => {
    if (nextCabinType !== selectedCabinType) {
      const nextSearch = setSearchParamValue(search, 'cabinTypes', nextCabinType === 'Oceanview / Window' ? ['Oceanview', 'Window'] : nextCabinType)

      dispatch(replace({ search: nextSearch }))
      setSelectedCabinType(nextCabinType)
    } else {
      const nextSearch = deleteSearchParams(search, 'cabinTypes')

      dispatch(replace({ search: nextSearch }))
      setSelectedCabinType(undefined)
    }
  }, [dispatch, search, selectedCabinType])

  const openModal = useCallback(async() => {
    const selectedCabinType = await showModal<string | undefined>(<CruiseCabinTypeInfoModal cabinTypeMap={mappedCabinTypes} cabinTypeLabels={cabinCategories} />)
    if (selectedCabinType) {
      handleSelectedCabinType(selectedCabinType)
    }
  }, [showModal, mappedCabinTypes, handleSelectedCabinType, cabinCategories])

  // Sync local state with URL params when cabinType changes
  useEffect(() => {
    setSelectedCabinType(cabinType)
  }, [cabinType])

  const fromCaption = intl.formatMessage({
    id: 'nl1u23SZIf',
    defaultMessage: 'From',
    description: 'Price caption indicating starting price for cabin type in search filter',
  })

  const perNightUnit = intl.formatMessage({
    id: '+d4n8q12+E',
    defaultMessage: '/night',
    description: 'Price unit suffix for per-night cabin type pricing in search filter',
  })

  const perPersonUnit = intl.formatMessage({
    id: '3yktjcFffY',
    defaultMessage: '/person',
    description: 'Price unit suffix for per-person cabin type pricing in search filter',
  })

  return <Group direction="vertical" gap={16}>
    <Group direction="horizontal" gap={16} horizontalAlign="space-between">
      <Heading variant="heading6">
        {intl.formatMessage({
          id: '1mm2b+pvFR',
          defaultMessage: 'Select a cabin type',
          description: 'Heading for the cabin type selection section in cruise search',
        })}
      </Heading>
      <TextLink size="small" weight="regular" onClick={openModal}>
        {intl.formatMessage({
          id: '14JpkyD4vp',
          defaultMessage: 'Which cabin type is right for me?',
          description: 'Link text to open a modal explaining the different cruise cabin types',
        })}
      </TextLink>
    </Group>
    <ToggleOptionContainer>
      <ToggleOptionsGroup horizontalAlign="start" desktopHorizontalAlign="stretch">
        {cabinCategories.map((cabinType) => (
          <ToggleOption
            key={cabinType}
            isSelected={selectedCabinType === cabinType}
            onClick={() => handleSelectedCabinType(cabinType)}
            subtitle={!isMobile && <Group direction="horizontal" verticalAlign="center" gap={2}>
              <PriceRowPriceWithCaption
                price={mappedCabinTypes[cabinType]?.[priceProperties]}
                size="S"
                caption={fromCaption}
                saleUnit={isPricePerNight ? perNightUnit : perPersonUnit}
              />
            </Group>}
            title={cabinTypeLabels[cabinType] ?? cabinType}
          />
        ))}
      </ToggleOptionsGroup>
    </ToggleOptionContainer>
  </Group>
}

export default CruiseSelectCabinType
